import { useRef, useState } from 'react';

export interface IProviderSwapWarningControls {
  showSwapWarningModal: boolean;
  // Opens the warning and resolves with the user's decision. The resolver is parked in a ref so
  // the calling swap flow can simply `await` it mid-sequence.
  confirmSwapWarning: () => Promise<boolean>;
  // Resolves the pending decision as confirmed but leaves the modal open (its buttons disable
  // themselves) so the caller can close it once the commit settles.
  handleSwapWarningConfirm: () => void;
  // Resolves the pending decision as declined and closes the modal.
  handleSwapWarningCancel: () => void;
  closeSwapWarningModal: () => void;
}

/**
 * Area-code warning gate for provider swaps (MES-2614). Owns the show/hide state of
 * ProviderSwapWarningModal and the promise the swap flow awaits while the user decides. Shared
 * by the Campaigns grid and Campaign Details so neither screen carries the plumbing itself.
 */
const useProviderSwapWarning = (): IProviderSwapWarningControls => {
  const [showSwapWarningModal, setShowSwapWarningModal] = useState(false);
  const swapConfirmRef = useRef<((confirmed: boolean) => void) | null>(null);

  const confirmSwapWarning = (): Promise<boolean> =>
    new Promise<boolean>((resolve) => {
      swapConfirmRef.current = resolve;
      setShowSwapWarningModal(true);
    });

  const handleSwapWarningConfirm = () => {
    swapConfirmRef.current?.(true);
    swapConfirmRef.current = null;
  };

  const handleSwapWarningCancel = () => {
    if (!swapConfirmRef.current) {
      return;
    }
    swapConfirmRef.current(false);
    swapConfirmRef.current = null;
    setShowSwapWarningModal(false);
  };

  const closeSwapWarningModal = () => setShowSwapWarningModal(false);

  return {
    showSwapWarningModal,
    confirmSwapWarning,
    handleSwapWarningConfirm,
    handleSwapWarningCancel,
    closeSwapWarningModal,
  };
};

export default useProviderSwapWarning;
